import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgForm, NgModel } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '../../core/services/user.service';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-password-recovery',
    templateUrl: './password-recovery.component.html',
    standalone: false
})
export class PasswordRecoveryComponent {
  loading = false;
  email = '';
  emailSent = false;
  emailError: string = '';
  submitted = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    private userService: UserService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private router: Router
  ) {}

  /**
   * Navigate to login page
   */
  goToLogin(): void {
    this.router.navigate(['/account/login']);
  }

  /**
   * Returns the appropriate client-side validation error message for a field.
   * Shows errors after the user has interacted with it (dirty or touched), or after a submit attempt.
   */
  getFieldError(field: NgModel): string {
    if (!field || !field.invalid || (!field.dirty && !field.touched && !this.submitted)) {
      return '';
    }
    if (field.errors?.['required']) {
      return this.translate.instant('AUTH.COMMON.EMAIL_REQUIRED');
    }
    if (field.errors?.['email']) {
      return this.translate.instant('AUTH.COMMON.EMAIL_INVALID');
    }
    return '';
  }

  onEmailChange(): void {
    this.emailError = '';
  }

  /**
   * Form submit
   */
  onSubmit(resetForm: NgForm): void {
    this.submitted = true;
    if (resetForm.invalid) {
      resetForm.control.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.emailError = '';
    this.emailSent = false;
    const data = {
      email: resetForm.value.email
    };

    this.userService.forgetPassword(data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (res) {
            // Show success message - user needs to check their email for the reset link
            this.emailSent = true;
          } else {
            this.confirmationService.swalError(this.translate.instant('notifications.auth.invalid_email'));
          }
        },
        error: (error: { status?: number, errors?: { email?: string[] }, message?: string, originalError?: { error?: { error?: string, message?: string } } }) => {
          this.loading = false;

          if (error?.status === 429) {
            // Rate-limited (route throttle, or the broker's own resend
            // cooldown) — not a field problem, so a modal fits better than
            // the inline field error used below.
            this.confirmationService.swalError(this.translate.instant('AUTH.PASSWORD_RECOVERY.TOO_MANY_ATTEMPTS'));
          } else if (error?.errors?.email?.[0]) {
            // Prefer the backend's specific validation message (required /
            // invalid format / not found) over the generic one below — this
            // is always what's actually present on a 422, so it must be
            // checked before assuming "not found".
            this.emailError = error.errors.email[0];
          } else if (error?.originalError?.error?.error === 'inactive_user' && error?.originalError?.error?.message) {
            this.emailError = error.originalError.error.message;
          } else if (error?.status === 422) {
            this.emailError = this.translate.instant('AUTH.PASSWORD_RECOVERY.EMAIL_NOT_FOUND');
          } else {
            this.confirmationService.swalError(
              error?.message || this.translate.instant('notifications.auth.invalid_email')
            );
          }
        },
      });
  }
}
